import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { NotificationFerme, NotificationsService } from 'src/app/core/services/entities/notifications.service';

/** Écrans de l'app mobile → routes du web */
const ECRANS: Record<string, string> = {
  '/analyst': '/analyste', '/unpaid': '/finances/impayes', '/articles': '/articles', '/stock': '/stock',
  '/finance': '/finances', '/invoices': '/factures', '/dashboard': '/dashboard',
  '/equipment': '/equipements', '/poultry': '/volaille', '/ponds': '/bassins', '/documents': '/ged',
};

/**
 * Cloche de la barre du haut : nombre de non lues (rafraîchi toutes les 2 minutes) et boîte de
 * notifications (rappels, constats de l'analyste, article à valider…). Même boîte que sur le mobile.
 */
@Component({
  selector: 'bay-notifications',
  template: `
    <div class="bay-notif">
      <button type="button" class="bay-notif-btn" (click)="basculer($event)" [attr.aria-expanded]="ouvert"
              [attr.aria-label]="nonLues ? nonLues + ' notification(s) non lue(s)' : 'Notifications'">
        <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
        <span *ngIf="nonLues" class="bay-notif-badge" data-etat="badge">{{ nonLues > 9 ? '9+' : nonLues }}</span>
      </button>
      <div *ngIf="ouvert" class="bay-notif-panel" role="dialog" aria-label="Notifications" (click)="$event.stopPropagation()">
        <div class="bay-notif-tete">
          <strong>Notifications</strong>
          <button *ngIf="nonLues" type="button" class="bay-notif-lien" (click)="toutLu()">Tout marquer comme lu</button>
        </div>
        <div *ngIf="chargement" class="bay-notif-vide">Chargement…</div>
        <div *ngIf="!chargement && erreur" class="bay-notif-vide" data-etat="erreur">Impossible de charger les notifications.</div>
        <div *ngIf="!chargement && !erreur && !elements.length" class="bay-notif-vide" data-etat="vide">Aucune notification pour le moment.</div>
        <button type="button" *ngFor="let n of elements" class="bay-notif-item" [class.bay-notif-nonlue]="!n.lu" (click)="ouvrir(n)">
          <span class="bay-notif-titre">{{ n.titre }}</span>
          <span class="bay-notif-corps">{{ n.corps }}</span>
          <span class="bay-notif-date">{{ n.date | date:'d MMM, HH:mm':undefined:'fr' }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .bay-notif { position: relative; }
    .bay-notif-btn { position: relative; border: none; background: transparent; width: 40px; height: 40px; border-radius: 12px;
      display: inline-flex; align-items: center; justify-content: center; color: #181d17; }
    .bay-notif-btn:hover { background: #e5eadf; }
    .bay-notif-badge { position: absolute; top: 4px; right: 3px; min-width: 18px; height: 18px; padding: 0 4px; border-radius: 9px;
      background: #c62828; color: #fff; font-size: 11px; font-weight: 700; line-height: 18px; text-align: center; }
    .bay-notif-panel { position: absolute; right: 0; top: 46px; width: min(360px, 92vw); max-height: 70vh; overflow-y: auto; z-index: 1060;
      background: #fff; border-radius: 16px; box-shadow: 0 10px 30px rgba(24,29,23,.18); padding: 6px; }
    .bay-notif-tete { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; }
    .bay-notif-lien { border: none; background: none; color: #0d631b; font-size: 12.5px; font-weight: 600; }
    .bay-notif-vide { padding: 18px 10px; color: #707a6c; font-size: 13.5px; text-align: center; }
    .bay-notif-item { display: grid; gap: 2px; width: 100%; text-align: left; border: none; background: transparent; padding: 10px; border-radius: 12px; }
    .bay-notif-item:hover { background: #f7fbf0; }
    .bay-notif-nonlue { background: #eef6ea; }
    .bay-notif-titre { font-weight: 700; color: #181d17; font-size: 13.5px; }
    .bay-notif-corps { color: #3f463c; font-size: 13px; }
    .bay-notif-date { color: #707a6c; font-size: 11.5px; }
  `]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  ouvert = false;
  nonLues = 0;
  elements: NotificationFerme[] = [];
  chargement = false;
  erreur = false;
  private sub?: Subscription;

  constructor(private service: NotificationsService, private router: Router) {}

  ngOnInit(): void {
    this.rafraichir();
    this.sub = interval(120_000).subscribe(() => this.rafraichir());
  }

  /** Compteur des non lues (en cas d'échec, la cloche reste utilisable) */
  rafraichir(): void {
    this.service.nonLues().subscribe({ next: r => this.nonLues = r.nonLues, error: () => {} });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  @HostListener('document:click')
  fermer(): void { this.ouvert = false; }

  basculer(ev: Event): void {
    ev.stopPropagation();
    this.ouvert = !this.ouvert;
    if (this.ouvert) { this.charger(); }
  }

  charger(): void {
    this.chargement = true;
    this.erreur = false;
    this.service.liste(0, 20).subscribe({
      next: p => { this.elements = p.elements; this.nonLues = p.nonLues; this.chargement = false; },
      error: () => { this.chargement = false; this.erreur = true; },
    });
  }

  ouvrir(n: NotificationFerme): void {
    if (!n.lu) {
      n.lu = true;
      this.service.lue(n.id).subscribe({ next: r => this.nonLues = r.nonLues, error: () => {} });
    }
    this.ouvert = false;
    this.router.navigateByUrl(NotificationsComponent.routeWeb(n.ecran));
  }

  toutLu(): void {
    this.service.toutLu().subscribe({ next: () => { this.nonLues = 0; this.elements.forEach(e => e.lu = true); }, error: () => {} });
  }

  static routeWeb(ecran?: string): string {
    if (!ecran) { return '/dashboard'; }
    if (ecran.startsWith('/ponds/')) { return '/bassins'; }
    return ECRANS[ecran] || (ecran.startsWith('/') ? ecran : '/dashboard');
  }
}
